/**
 * deviceList.component.tsx
 * Secured Communication
 *
 * Created by: Gabrielle Lindberg
 * Copyright (c) 2025 Secured Communications. All rights reserved.
 *
**/

import React, { useEffect } from "react";
import { Link } from "react-router-dom";

import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import { Breadcrumbs, CircularProgress, IconButton, TextField, DialogContentText, Button, FormControl, Select, MenuItem, SelectChangeEvent } from "@mui/material";
import ComputerIcon from "@mui/icons-material/Computer";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import WatchIcon from "@mui/icons-material/Watch";
import TvIcon from "@mui/icons-material/Tv";



import { Device, DeviceType } from "../models/auth";
import { authServiceFactory } from "../services/auth.service";
import { groupServiceFactory } from "../services/group.service";
import { ErrorSnackBar } from "../components/errorSnackBar.component";
import { Dialog } from "../components/dialog.component";

interface DeviceEntry {
  id: string,
  display: string,
  lastSeen: string,
  device: Device,
  myDevice: boolean,
}
const DEVICE_TIME_OPTIONS: { [index: string]: number | null } = {
  "Never": null,
  "1 hour": 60,
  "8 hours": 480,
  "12 hours": 720,
  "1 day": 1440,
  "1 week": 10080,
  "30 days": 43200,
  "60 days": 86400,
  "90 days": 129600,
  "120 days": 172800,
  "1 year": 525600,
};


type DeviceImageMap = {
  [K in DeviceType]: any;
};
const deviceImageMap: DeviceImageMap = {
  web: <ComputerIcon className="device-icon" />,
  mobile: <PhoneIphoneIcon className="device-icon" />,
  watch: <WatchIcon className="device-icon" />,
  desktop: <ComputerIcon className="device-icon" />,
  tv: <TvIcon className="device-icon" />
}

export function DeviceListComponent(): React.ReactElement<any, any> {
  const [devices, setDevices] = React.useState<Array<DeviceEntry>>([]);
  const [loading, setLoading] = React.useState(false);
  const [errMsg, setErrMsg] = React.useState("");
  const [newDeviceName, setNewDeviceName] = React.useState("")
  const [editingDevice, setEditingDevice] = React.useState<DeviceEntry | null>(null);
  const [deviceNameError, setDeviceNameError] = React.useState("");
  const [logoutDevice, setDeviceLogout] = React.useState<DeviceEntry | null>(null);
  const [idleTime, setIdleTime] = React.useState<string>("0");
  const [sessionTime, setSessionTime] = React.useState<string>("0");
  const testing = true;


  useEffect(() => {
    setLoading(true);
    const authService = authServiceFactory();
    if (!authService.currentAccountMembership) {
      console.error("Account membership not found");
      setErrMsg("Account membership not found");
      return;
    }

    authServiceFactory().getMemberDevices(authService.currentAccountMembership.userID)
      .then((memberDevices: Device[]) => {
        processDevices(memberDevices);
        setLoading(false);
      }, err => {
        console.error("Error fetching member devices:", err);
        setErrMsg((err as Error).message);
      });
  }, []);

  function processDevices(memDevices: Device[]) {
    const groupService = groupServiceFactory();
    const myDevice = groupService.deviceID;

    const deviceList: DeviceEntry[] = memDevices.map(device => {
      const entry: DeviceEntry = {
        id: device._id,
        display: device.displayString ? device.displayString : device._id,
        lastSeen: device.modified ? device.modified.format("MMM D, YYYY h:mma") : "Unknown",
        device: device,
        myDevice: device._id === myDevice
      };
      if (entry.myDevice) {
        entry.display += " (This Device)";
      }
      return entry;
    });

    deviceList.sort((a, b) => {
      if (a.device.modified && b.device.modified) {
        if (a.device.modified.isAfter(b.device.modified)) { return -1; }
        if (a.device.modified.isBefore(b.device.modified)) { return 1; }
        return 0;
      }
      // unknown times put at the bottom of the list
      return !a.device.modified ? (!b.device.modified ? 0 : 1) : -1;
    });
    setDevices(deviceList);
  }

  async function removeDevice(device: DeviceEntry) {
    if (device.myDevice) {
      setErrMsg("Cannot remove current device");
      return;
    }
    try {
      await authServiceFactory().deleteDevice(device.device._id)
      const remainingDevices = [...devices];
      const index = remainingDevices.indexOf(device);
      if (index < 0) {
        setErrMsg("Device not found");
        return;
      }
      remainingDevices.splice(index, 1);
      setDevices(remainingDevices);
      setEditingDevice(null);
    } catch (err) {
      console.error("Error removing device:", err);
      setErrMsg((err as Error).message);
    }
  }

  /**
   * Save the new device name and then update the device list to reflect the change from the server
   * @returns 
   */
  async function onConfirmDialog() {
    //todo future updates coming with session and idle timeouts...
    if (!newDeviceName || newDeviceName.length < 0) {
      setDeviceNameError("Device name must be longer thatn 0 characters!")
      return;
    }
    if (!editingDevice) return;

    try {
      const updatedDevice = await authServiceFactory().updateDeviceName(editingDevice.id, newDeviceName)
      const updatedDevices = devices.map(device => {
        if (device.id === updatedDevice.id) {
          return {
            ...device,
            display: updatedDevice.displayName
          }
        }
        return device;
      })
      setEditingDevice(null)
      setDevices(updatedDevices);

    }
    catch (err) {
      console.error("Error updating device name:", err);
      setDeviceNameError((err as Error).message);
    }

  }

  const onEditDeviceName = (device: DeviceEntry) => {
    setEditingDevice(device)
  }


  function renderDevice(device: DeviceEntry) {
    return (
      <div key={device.id} className="device-item device-item-border" >
        <div className="left-display">
          {deviceImageMap[device.device.devicetype]}
          <div className="device-details" >
            <span className="device-name-text">{device.device.platform}</span>
            <span>{device.display}</span>
            <span className="light-text">Last active: {device.lastSeen}</span>
          </div>
        </div>
        <div className="right-display">
          <IconButton size="small" onClick={() => onEditDeviceName(device)}>
            <SettingsOutlinedIcon />
          </IconButton>
          {!testing && <IconButton size="small" onClick={() => setDeviceLogout(device)}>
            <LogoutOutlinedIcon />
          </IconButton>}

        </div>
      </div>)
  }

  return <>
    <div className="main-section-container main-scroll-container">
      <div className="main-section-inner-with-header">
        <Breadcrumbs aria-label="breadcrumb" className="main-breadcrumbs">
          <Link to="/settings">
            <NavigateBeforeIcon fontSize="small" />
            <span>Back to Settings</span>
          </Link>
        </Breadcrumbs>

        <div className="profile-form-container">
          <h2>My Devices</h2>
          <p className="my-devices-info">
            Manage your devices and sessions.
          </p><br />
          <div className="device-container">
            {loading ? <CircularProgress size={20} /> : devices.length ?
              devices.map(device => renderDevice(device)) :
              <ErrorSnackBar errMsg={"No Devices Found"} didClose={() => setErrMsg("")} />
            }
          </div>
        </div>
        <ErrorSnackBar errMsg={errMsg} didClose={() => setErrMsg("")} />
      </div>
      <Dialog
        fullWidth={true}
        title={"Device Settings"}
        open={editingDevice !== null}
        onClose={() => setEditingDevice(null)}
        loading={false}
        onConfirm={onConfirmDialog}>
        <div className="device-settings-dialog">
          <div>
            <span>Device Name</span>
            <DialogContentText>
              This will change the device display name across your current account.
            </DialogContentText>
            <FormControl fullWidth className="form-row">
              <TextField
                className="edit-device-name"
                fullWidth={true}
                label="Rename Device"
                placeholder={editingDevice?.display}
                variant="outlined"
                value={newDeviceName}
                onChange={(e) => setNewDeviceName(e.target.value)} />
            </FormControl>
          </div>
          {!testing &&
            <div>
              <div >
                <span>Idle Timeout</span>
                <DialogContentText>
                  This is the amount of time your device can be idle before having to login again.
                </DialogContentText>
                <div >
                  <Select
                    value={idleTime}
                    className="time-options"
                    onChange={(event: SelectChangeEvent) => { setIdleTime(event.target.value as string) }}
                  >
                    {Object.keys(DEVICE_TIME_OPTIONS).map((time => {
                      return (<MenuItem key={time} value={time}> {time}</MenuItem>)
                    }))}
                  </Select>
                </div>
              </div>
              <div >
                <span>Max Session Timeout</span>
                <DialogContentText>
                  This is the max amount of time before you will be forced to login again.
                </DialogContentText>
                <div >
                  <Select
                    value={sessionTime}
                    className="time-options"
                    onChange={(event: SelectChangeEvent) => { setSessionTime(event.target.value as string) }}
                  >
                    {Object.keys(DEVICE_TIME_OPTIONS).map((time => {
                      return (<MenuItem key={time} value={time}> {time}</MenuItem>)
                    }))}
                  </Select>
                </div>
              </div>
            </div>
          }
          {editingDevice && !editingDevice.myDevice && <div >
            <span>Delete Device</span>
            <DialogContentText>
              Remove current device from account, logging back in at a
              later time will restore it and any data that has not been deleted.
            </DialogContentText>
            <Button
              color="error"
              variant="contained"
              onClick={() => removeDevice(editingDevice as DeviceEntry)}>Delete Device</Button>
          </div>}


          <ErrorSnackBar errMsg={deviceNameError} didClose={() => setDeviceNameError("")} />

        </div>
      </Dialog>
      <Dialog
        fullWidth={true}
        title={"Confirm device logout"}
        open={logoutDevice !== null}
        onClose={() => setDeviceLogout(null)}
        loading={false}
        onConfirm={() => console.log("TODO")}>
        <div>
          <DialogContentText>
            Are you sure you want to log out of this device?
          </DialogContentText>
        </div>
      </Dialog>


    </div>
  </>
};